import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from "react";

export const panelSelectTriggerClass = "h-8 w-full border-white/10 bg-white/[0.04] text-[12px] text-slate-200";
export const panelSelectContentClass = "border-white/10 bg-[#242a31] text-slate-200";
export const panelSelectItemClass = "text-[12px] focus:bg-sky-500/20 focus:text-sky-100";
export const panelFloatingUiAttribute = "data-curva-panel-floating-ui";
export const panelFloatingUiCloseEvent = "curva:panel-floating-ui-close";

export function closePanelFloatingUi(): void {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new Event(panelFloatingUiCloseEvent));
}

export function PanelSelect<T extends string>({
  value,
  placeholder = "Mixed",
  onValueChange,
  triggerClassName,
  contentClassName,
  contentPosition = "popper",
  contentAvoidCollisions,
  disabled,
  children,
}: {
  value: T | "mixed";
  placeholder?: string;
  onValueChange: (value: T) => void;
  triggerClassName?: string;
  contentClassName?: string;
  contentPosition?: "popper" | "item-aligned";
  contentAvoidCollisions?: boolean;
  disabled?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(event: PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (triggerRef.current?.contains(target) || contentRef.current?.contains(target)) {
        return;
      }
      if (target instanceof Element && target.closest(`[${panelFloatingUiAttribute}]`)) {
        return;
      }
      setOpen(false);
    }

    document.addEventListener("pointerdown", handlePointerDown, { capture: true });
    return () => document.removeEventListener("pointerdown", handlePointerDown, { capture: true });
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handleCloseRequest(): void {
      setOpen(false);
    }

    function handleWheel(event: WheelEvent): void {
      const target = event.target;
      if (target instanceof Node && (triggerRef.current?.contains(target) || contentRef.current?.contains(target))) {
        return;
      }
      if (target instanceof Element && target.closest(`[${panelFloatingUiAttribute}]`)) {
        return;
      }
      setOpen(false);
    }

    window.addEventListener(panelFloatingUiCloseEvent, handleCloseRequest);
    window.addEventListener("wheel", handleWheel, { capture: true, passive: true });
    return () => {
      window.removeEventListener(panelFloatingUiCloseEvent, handleCloseRequest);
      window.removeEventListener("wheel", handleWheel, { capture: true });
    };
  }, [open]);

  return (
    <Select
      open={open}
      onOpenChange={setOpen}
      value={value === "mixed" ? undefined : value}
      onValueChange={next => {
        onValueChange(next as T);
        setOpen(false);
      }}
      disabled={disabled}
    >
      <SelectTrigger ref={triggerRef} size="sm" className={cn(panelSelectTriggerClass, triggerClassName)} disabled={disabled}>
        <SelectValue placeholder={value === "mixed" ? placeholder : undefined} />
      </SelectTrigger>
      <SelectContent
        ref={contentRef}
        className={cn(panelSelectContentClass, contentClassName)}
        position={contentPosition}
        avoidCollisions={contentAvoidCollisions}
        onWheel={event => event.stopPropagation()}
        {...{ [panelFloatingUiAttribute]: "" }}
      >
        {children}
      </SelectContent>
    </Select>
  );
}

export function PanelSelectItem({ className, ...props }: ComponentProps<typeof SelectItem>) {
  return <SelectItem className={cn(panelSelectItemClass, className)} {...props} />;
}

export function PanelLabeledSelect<T extends string>({
  label,
  value,
  placeholder,
  options,
  onChange,
  triggerClassName,
  disabled,
}: {
  label: string;
  value: T | "mixed";
  placeholder?: string;
  options: Array<{ value: T; label: ReactNode }>;
  onChange: (value: T) => void;
  triggerClassName?: string;
  disabled?: boolean;
}) {
  return (
    <label className="grid gap-1">
      <span className="text-[11px] text-slate-500">{label}</span>
      <PanelSelect
        value={value}
        placeholder={placeholder}
        onValueChange={onChange}
        triggerClassName={triggerClassName}
        disabled={disabled}
      >
        {options.map(option => (
          <PanelSelectItem key={option.value} value={option.value}>
            {option.label}
          </PanelSelectItem>
        ))}
      </PanelSelect>
    </label>
  );
}
